import { useQuery } from '@apollo/client';
import { gql } from '__generated__/gql';
import { FundClosingDrawdownType, FundClosingType } from '__generated__/graphql';
import { FC, useState } from 'react';
import { useParams } from 'react-router-dom';

import SidebarLayout, {
  SidebarLayoutContent,
  SidebarLayoutNavigation,
} from 'layouts/SidebarLayout';

import DataTable, { Column } from 'components/DataTable';
import ErrorMessage from 'components/ErrorMessage';
import Statistics from 'components/Statistics';

import LoadingIndicator from 'primitives/LoadingIndicator';

import manageIcon from 'media/icons/manage.svg';
import outgoingInvitesIcon from 'media/icons/outgoing-invites.svg';

import FundClosingDrawdown from '../FundClosingDrawdown/fund-closing-drawdown';

const FUND_CLOSINGS_QUERY = gql(`
  query GetFundClosings($fundId: ID!) {
    fund(id: $fundId) {
      id
      statistics {
        totalRaisedAmount
        totalInvestedAmount
        balanceAmount
        numberOfClosings
        fundStatisticsBreakdownByV2FundClosing  {
          totalCommittedAmount
          totalDrawdownAmount
        }
      }
      syndicate {
        id
      }
      fundClosings {
        nodes {
          id
          status
          createdAt
          wiredAt
          amount
          transferredAmount
          investmentEntity {
            name
            user {
              id
            }
          }
        }
      }
    }
  }
`);

const FUND_CLOSING_DRAWDOWN_QUERY_BY_USER_ID = gql(`
  query GetFundClosingDrawdown($fundId: ID!, $userId: ID!) {
    fund(id: $fundId) { 
      userFundClosingDrawdown(userId: $userId) {
        id
        amount
        status
        createdAt
        drawdownDate
      }
    }
  }
`);

const FundClosings: FC = () => {
  const { fundId } = useParams<{ fundId: string }>() as { fundId: string };

  const tabs = [
    {
      key: 'manage',
      label: 'Manage',
      description: 'Manage the LPs who can invest in your deals',
      route: `/fund-dashboard/${fundId}/partners`,
      icon: manageIcon,
      exact: true,
    },
    {
      key: 'invites',
      label: 'Invites',
      description: 'View and manage outgoing invites to LPs',
      route: `/fund-dashboard/${fundId}/partners/invites`,
      icon: outgoingInvitesIcon,
    },
  ];

  const { loading, error, data, refetch } = useQuery(FUND_CLOSINGS_QUERY, {
    variables: {
      fundId,
    },
  });

  const [showDrawdown, setShowDrawdown] = useState(false);
  const [selectedClosing, setSelectedClosing] = useState<FundClosingType | null>(null);

  const { loading: drawdownLoading, data: drawdownData } = useQuery(
    FUND_CLOSING_DRAWDOWN_QUERY_BY_USER_ID,
    {
      variables: {
        fundId,
        userId: selectedClosing?.investmentEntity?.user?.id || '',
      },
    }
  );

  const renderContent = () => {
    if (loading) return <LoadingIndicator />;

    if (error || !data || !data.fund) return <ErrorMessage error={error} refetch={refetch} />;

    const closings = data.fund.fundClosings.nodes;

    const getDataColumns = (isV2Fund: boolean) => {
      return [
        { label: 'Name', fieldName: 'investmentEntity.name' },
        { label: 'Status', fieldName: 'status', type: 'STATUS' },

        ...(!isV2Fund ? [{ label: 'Invested On', fieldName: 'wiredAt', type: 'DATE' }] : []),
        ...(isV2Fund ? [{ label: 'Committed On', fieldName: 'createdAt', type: 'DATE' }] : []),
        {
          label: isV2Fund ? 'Committed Amount' : 'Amount',
          fieldName: 'amount',
          type: 'CURRENCY',
        },
        ...(isV2Fund
          ? [{ label: 'Drawdown Amount', fieldName: 'transferredAmount', type: 'CURRENCY' }]
          : []),
      ];
    };

    const dataColumns = getDataColumns(!!data?.fund?.syndicate?.id);

    return (
      <>
        {data?.fund?.syndicate?.id ? (
          <Statistics
            statistics={[
              {
                label: 'Total Committed Amount',
                value:
                  data.fund.statistics.fundStatisticsBreakdownByV2FundClosing.totalCommittedAmount,
                type: 'CURRENCY',
              },
              {
                label: 'Total Drawdown Amount',
                value:
                  data.fund.statistics.fundStatisticsBreakdownByV2FundClosing.totalDrawdownAmount,
                type: 'CURRENCY',
              },
            ]}
          />
        ) : (
          <Statistics
            statistics={[
              {
                label: 'Total Raised',
                value: data.fund.statistics.totalRaisedAmount,
                type: 'CURRENCY',
              },
              {
                label: 'Total Invested',
                value: data.fund.statistics.totalInvestedAmount,
                type: 'CURRENCY',
              },
              {
                label: 'Balance',
                value: data.fund.statistics.balanceAmount,
                type: 'CURRENCY',
              },
              {
                label: 'Number of LPs',
                value: data.fund.statistics.numberOfClosings,
                type: 'NUMBER',
              },
            ]}
          />
        )}

        {showDrawdown && (
          <FundClosingDrawdown
            loading={drawdownLoading}
            showDrawdown={showDrawdown}
            setShowDrawdown={setShowDrawdown}
            fundClosingDrawdown={
              drawdownData?.fund?.userFundClosingDrawdown as FundClosingDrawdownType[]
            }
          />
        )}
        <DataTable
          onClick={dataItem => {
            if (data?.fund?.syndicate?.id) {
              setShowDrawdown(true);
              setSelectedClosing(dataItem);
            }
          }}
          data={closings}
          columns={dataColumns as Column[]}
        />
      </>
    );
  };

  return (
    <SidebarLayout>
      <SidebarLayoutNavigation
        title="Limited Partners"
        subTitle="View the investors of your fund here"
      />
      {data?.fund?.syndicate?.id ? (
        <SidebarLayoutContent secondaryTabs={tabs}>{renderContent()}</SidebarLayoutContent>
      ) : (
        <SidebarLayoutContent>{renderContent()}</SidebarLayoutContent>
      )}
    </SidebarLayout>
  );
};

export default FundClosings;
